import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Chip, CircularProgress, Collapse, IconButton, Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { labelSx, estiloTabla } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

function hoy() { return new Date().toISOString().split('T')[0]; }
function offset(dias) {
  const d = new Date(); d.setDate(d.getDate() + dias); return d.toISOString().split('T')[0];
}
function inicioSemana() {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
  return d.toISOString().split('T')[0];
}
function inicioMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const TIPO_CONFIG = {
  cobrado:   { label: 'Cobrado',   color: '#81c784', bg: 'rgba(76,175,80,0.12)',   border: 'rgba(76,175,80,0.3)' },
  exento:    { label: 'Exento',    color: '#90a4ae', bg: 'rgba(176,190,197,0.08)', border: 'rgba(176,190,197,0.25)' },
  COBRADO:   { label: 'Cobrado',   color: '#81c784', bg: 'rgba(76,175,80,0.12)',   border: 'rgba(76,175,80,0.3)' },
  EXENTO:    { label: 'Exento',    color: '#90a4ae', bg: 'rgba(176,190,197,0.08)', border: 'rgba(176,190,197,0.25)' },
  CORREGIDO: { label: 'Corregido', color: '#ef5350', bg: 'rgba(239,83,80,0.08)',   border: 'rgba(239,83,80,0.25)' },
  ANULADO:   { label: 'Anulado',   color: '#546e7a', bg: 'rgba(84,110,122,0.08)', border: 'rgba(84,110,122,0.2)' },
};

function fmtFecha(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}
function fmtFechaSolo(iso) {
  if (!iso) return '-';
  const dateStr = String(iso).split('T')[0];
  try {
    const d = new Date(dateStr + 'T12:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  } catch { return dateStr; }
}
function fmtMonto(n) {
  if (!n && n !== 0) return '-';
  return `$${parseFloat(n).toLocaleString('es-AR', { minimumFractionDigits: 0 })}`;
}

function LF({ label, children }) {
  return <Box><Typography sx={labelSx}>{label}</Typography>{children}</Box>;
}

function BtnFecha({ label, active, onClick }) {
  return (
    <Box component="button" onClick={onClick} sx={{
      px: '12px', py: '6px', border: '1px solid',
      borderColor: active ? 'var(--c-acento)' : 'rgba(144,202,249,0.2)',
      bgcolor: active ? 'rgba(144,202,249,0.12)' : 'transparent',
      color: active ? 'var(--c-acento)' : '#607d8b',
      borderRadius: '6px', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
    }}>
      {label}
    </Box>
  );
}

function SummaryCard({ tipo, data }) {
  const cfg = TIPO_CONFIG[tipo] || TIPO_CONFIG[tipo?.toLowerCase()] || { label: tipo, color: '#90a4ae', bg: 'transparent', border: 'rgba(144,202,249,0.15)' };
  const cantidad = data?.cantidad || 0;
  const total = data?.total || 0;
  return (
    <Box sx={{ flex: 1, minWidth: 130, border: `1px solid ${cfg.border}`, bgcolor: cfg.bg, borderRadius: '8px', p: '14px 16px' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: 1, mb: '6px' }}>
        {cfg.label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: cfg.color, lineHeight: 1 }}>
        {tipo === 'cobrado' ? fmtMonto(total) : cantidad}
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#546e7a', mt: '4px' }}>
        {tipo === 'cobrado' ? `${cantidad} cobro${cantidad !== 1 ? 's' : ''}` : `registro${cantidad !== 1 ? 's' : ''}`}
      </Typography>
    </Box>
  );
}

const TITULO_ROL = {
  admin:         { titulo: 'Caja de coseguros',  subtitulo: 'Estado vigente del coseguro por período — fuente: turnos' },
  recepcionista: { titulo: 'Caja del día',        subtitulo: 'Todos los coseguros registrados en el período' },
  medico:        { titulo: 'Mis honorarios',     subtitulo: 'Coseguros de tus pacientes en el período' },
};

export default function Cajas({ usuario }) {
  const rol = usuario?.rol || 'admin';
  const { titulo: tituloBase, subtitulo } = TITULO_ROL[rol] || TITULO_ROL.admin;

  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [atajo, setAtajo] = useState('hoy');
  const [movimientos, setMovimientos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [sinResolver, setSinResolver] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sinResolverOpen, setSinResolverOpen] = useState(true);
  const [medicos, setMedicos] = useState([]);
  const [medicoId, setMedicoId] = useState('');

  useEffect(() => {
    if (rol === 'medico') return;
    axios.get(`${API}/api/medicos`).then(r => setMedicos(r.data || [])).catch(() => setMedicos([]));
  }, [rol]);

  const medicoQS = medicoId ? `&medico_id=${medicoId}` : '';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [movR, resR, srR] = await Promise.all([
        axios.get(`${API}/api/coseguros?desde=${desde}&hasta=${hasta}${medicoQS}`),
        axios.get(`${API}/api/coseguros/resumen?desde=${desde}&hasta=${hasta}${medicoQS}`),
        axios.get(`${API}/api/coseguros/sin-resolver?desde=${desde}&hasta=${hasta}${medicoQS}`),
      ]);
      const movs = movR.data.data || [];
      // Un movimiento es "inactivo" si existe un ANULADO posterior para el mismo turno
      // Esto maneja correctamente el caso COBRADO → ANULADO → COBRADO (el último COBRADO queda activo)
      setMovimientos(movs.map(m => ({
        ...m,
        _turnoAnulado: movs.some(
          x => x.turno_id === m.turno_id && x.tipo === 'ANULADO' && new Date(x.created_at) > new Date(m.created_at)
        ),
      })));
      setResumen(resR.data);
      setSinResolver(srR.data.data || []);
    } catch {}
    setLoading(false);
  }, [desde, hasta, medicoQS]);

  useEffect(() => { cargar(); }, [cargar]);

  function aplicarAtajo(key, d, h) { setAtajo(key); setDesde(d); setHasta(h); }

  const tasa = resumen?.tasa_resolucion;
  const tasaColor = tasa === null ? '#546e7a' : tasa >= 90 ? '#81c784' : tasa >= 70 ? '#ffb74d' : '#ef5350';

  const medicoSel = medicos.find(m => String(m.id) === String(medicoId));
  const titulo = medicoSel ? `${tituloBase} — ${medicoSel.nombre}` : tituloBase;

  return (
    <Box id="area-imprimible" sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'var(--c-texto)', fontWeight: 700, fontSize: 18 }}>
            {titulo}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#546e7a', mt: '2px' }}>
            {subtitulo}
          </Typography>
        </Box>
        <Button className="no-imprimir" variant="outlined" startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ color: 'var(--c-acento)', borderColor: 'rgba(144,202,249,0.3)', flexShrink: 0,
            '&:hover': { borderColor: 'var(--c-acento)', bgcolor: 'rgba(144,202,249,0.08)' } }}>
          Imprimir
        </Button>
      </Box>

      {/* Filtros */}
      <Box className="no-imprimir" sx={{
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end',
        p: '16px', bgcolor: 'var(--c-superficie)', borderRadius: '8px',
        border: '1px solid rgba(144,202,249,0.1)',
      }}>
        <LF label="Desde">
          <input type="date" value={desde} onChange={e => { setDesde(e.target.value); setAtajo(''); }}
            style={{ height: 40, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(144,202,249,0.2)', background: 'var(--c-fondo)', color: 'var(--c-texto)', fontSize: 13, colorScheme: 'dark', outline: 'none', cursor: 'pointer' }} />
        </LF>
        <LF label="Hasta">
          <input type="date" value={hasta} onChange={e => { setHasta(e.target.value); setAtajo(''); }}
            style={{ height: 40, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(144,202,249,0.2)', background: 'var(--c-fondo)', color: 'var(--c-texto)', fontSize: 13, colorScheme: 'dark', outline: 'none', cursor: 'pointer' }} />
        </LF>
        {rol !== 'medico' && (
          <LF label="Médico">
            <select value={medicoId} onChange={e => setMedicoId(e.target.value)}
              style={{ height: 40, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(144,202,249,0.2)', background: 'var(--c-fondo)', color: 'var(--c-texto)', fontSize: 13, outline: 'none', cursor: 'pointer', minWidth: 160 }}>
              <option value="">Todos</option>
              {medicos.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </LF>
        )}
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignSelf: 'flex-end' }}>
          <BtnFecha label="Hoy"         active={atajo === 'hoy'}    onClick={() => aplicarAtajo('hoy',    hoy(),        hoy())} />
          <BtnFecha label="Ayer"        active={atajo === 'ayer'}   onClick={() => aplicarAtajo('ayer',   offset(-1),   offset(-1))} />
          <BtnFecha label="Esta semana" active={atajo === 'semana'} onClick={() => aplicarAtajo('semana', inicioSemana(), hoy())} />
          <BtnFecha label="Este mes"    active={atajo === 'mes'}    onClick={() => aplicarAtajo('mes',    inicioMes(),  hoy())} />
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

      {!loading && resumen && (
        <>
          {/* Alerta sin resolver */}
          {sinResolver.length > 0 && (
            <Box sx={{
              border: '1px solid rgba(255,152,0,0.4)', bgcolor: 'rgba(255,152,0,0.06)',
              borderRadius: '8px', overflow: 'hidden',
            }}>
              <Box sx={{ px: '16px', py: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setSinResolverOpen(o => !o)}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#ffb74d' }}>
                  ⚠ Sin resolver: {sinResolver.length} turno{sinResolver.length !== 1 ? 's' : ''}
                </Typography>
                <IconButton size="small" sx={{ color: '#ffb74d' }}>
                  {sinResolverOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={sinResolverOpen}>
                <Box sx={{ borderTop: '1px solid rgba(255,152,0,0.2)' }}>
                  {sinResolver.map((t, i) => (
                    <Box key={t.id} sx={{
                      px: '16px', py: '10px', display: 'flex', gap: '16px', alignItems: 'center',
                      borderBottom: i < sinResolver.length - 1 ? '1px solid rgba(255,152,0,0.1)' : 'none',
                    }}>
                      <Typography sx={{ fontSize: 12, color: '#90a4ae', minWidth: 50 }}>
                        {t.hora_inicio?.substring(0, 5)}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: 'var(--c-texto)', flex: 1, fontWeight: 500 }}>
                        {t.paciente_nombre}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#607d8b', flex: 1 }}>
                        {t.medico_nombre || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#546e7a' }}>
                        {fmtFechaSolo(t.fecha)}
                      </Typography>
                      <Chip label={t.estado} size="small"
                        sx={{ fontSize: 10, height: 18, color: '#90a4ae', bgcolor: 'rgba(144,164,174,0.1)' }} />
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Cards de resumen + tasa + anulaciones */}
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'stretch' }}>
            {['cobrado', 'exento'].map(tipo => (
              <SummaryCard key={tipo} tipo={tipo} data={resumen.totales?.[tipo]} />
            ))}

            {/* Tasa de resolución */}
            <Box sx={{
              flex: 1, minWidth: 130,
              border: `1px solid rgba(144,202,249,0.15)`,
              bgcolor: 'transparent', borderRadius: '8px', p: '14px 16px',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: 1, mb: '6px' }}>
                Resolución
              </Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: tasaColor, lineHeight: 1 }}>
                {tasa !== null ? `${tasa}%` : '—'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#455a64', mt: '4px' }}>
                {resumen.total_atendidos} atendido{resumen.total_atendidos !== 1 ? 's' : ''},&nbsp;
                {resumen.sin_resolver} sin resolver
              </Typography>
            </Box>

            {/* Anulaciones — KPI */}
            {rol === 'admin' && resumen.anulaciones && (
              <Box sx={{
                flex: 1, minWidth: 130,
                border: `1px solid rgba(239,83,80,0.2)`,
                bgcolor: 'rgba(239,83,80,0.04)', borderRadius: '8px', p: '14px 16px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ef5350', textTransform: 'uppercase', letterSpacing: 1, mb: '6px' }}>
                  Anulaciones
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: resumen.anulaciones.hoy > 0 ? '#ef5350' : '#546e7a', lineHeight: 1 }}>
                  {resumen.anulaciones.hoy}
                </Typography>
                <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Typography sx={{ fontSize: 10, color: '#546e7a' }}>Hoy: {resumen.anulaciones.hoy}</Typography>
                  <Typography sx={{ fontSize: 10, color: '#546e7a' }}>Esta semana: {resumen.anulaciones.semana}</Typography>
                  <Typography sx={{ fontSize: 10, color: '#546e7a' }}>Este mes: {resumen.anulaciones.mes}</Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Desglose por forma de pago */}
          {resumen.por_medio_pago?.length > 0 && (
            <Box sx={{ p: '16px', bgcolor: 'var(--c-superficie)', borderRadius: '8px', border: '1px solid rgba(76,175,80,0.2)' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#81c784', textTransform: 'uppercase', letterSpacing: 1, mb: '12px' }}>
                Desglose cobrado por forma de pago
              </Typography>
              <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {resumen.por_medio_pago.map(mp => (
                  <Box key={mp.medio_pago}>
                    <Typography sx={{ fontSize: 11, color: '#546e7a', mb: '2px' }}>{mp.medio_pago || 'Sin especificar'}</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#81c784' }}>{fmtMonto(mp.total)}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#455a64' }}>{mp.cantidad} cobro{mp.cantidad !== 1 ? 's' : ''}</Typography>
                  </Box>
                ))}
                <Box sx={{ ml: 'auto', pl: '20px', borderLeft: '1px solid rgba(144,202,249,0.1)' }}>
                  <Typography sx={{ fontSize: 11, color: '#546e7a', mb: '2px' }}>Total general</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#81c784' }}>
                    {fmtMonto(resumen.totales?.cobrado?.total || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Tabla de movimientos (audit trail) */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: 1 }}>
                Registro de auditoría
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#37474f' }}>
                — todos los movimientos del período ({movimientos.length})
              </Typography>
            </Box>
            {movimientos.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: '#455a64', fontSize: 13 }}>
                No hay movimientos en el período seleccionado
              </Box>
            ) : (
              <Paper sx={{ ...estiloTabla, overflow: 'hidden', border: '1px solid rgba(144,202,249,0.1)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(144,202,249,0.04)' }}>
                      {['Fecha/Hora', 'Paciente', 'Médico', 'Tipo', 'Concepto', 'Monto', 'Forma de pago', 'Registrado por', 'Motivo'].map(h => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: 0.8, py: '8px', borderColor: 'rgba(144,202,249,0.08)' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientos.map((mov, i) => {
                      const cfg = TIPO_CONFIG[mov.tipo] || { label: mov.tipo, color: '#90a4ae', bg: 'transparent', border: 'rgba(144,202,249,0.1)' };
                      // Cobros de un turno anulado → visualmente apagados, no cuentan en el resumen
                      const esAnuladoInactivo = mov._turnoAnulado && mov.tipo !== 'ANULADO';
                      const rowOpacity = esAnuladoInactivo ? 0.38 : 1;
                      return (
                        <TableRow key={mov.id} sx={{
                          bgcolor: i % 2 === 0 ? 'transparent' : 'rgba(144,202,249,0.02)',
                          '&:hover': { bgcolor: 'rgba(144,202,249,0.05)' },
                          opacity: rowOpacity,
                        }}>
                          <TableCell sx={{ fontSize: 12, color: '#546e7a', borderColor: 'rgba(144,202,249,0.06)', whiteSpace: 'nowrap' }}>
                            {fmtFecha(mov.created_at)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: 'var(--c-texto)', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.paciente_nombre || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#546e7a', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.medico_nombre || '-'}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(144,202,249,0.06)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Chip label={cfg.label} size="small"
                                sx={{ fontSize: 10, fontWeight: 700, height: 20, color: cfg.color, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
                                  textDecoration: esAnuladoInactivo ? 'line-through' : 'none' }} />
                              {esAnuladoInactivo && (
                                <Typography sx={{ fontSize: 9, color: '#546e7a', fontStyle: 'italic' }}>anulado</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#90a4ae', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.concepto || 'Coseguro'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 700,
                            color: esAnuladoInactivo ? '#37474f' : (mov.monto ? '#81c784' : '#37474f'),
                            textDecoration: esAnuladoInactivo ? 'line-through' : 'none',
                            borderColor: 'rgba(144,202,249,0.06)' }}>
                            {fmtMonto(mov.monto)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#546e7a', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.medio_pago || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#546e7a', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.usuario_nombre}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, color: '#455a64', fontStyle: 'italic', borderColor: 'rgba(144,202,249,0.06)' }}>
                            {mov.motivo || ''}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
